/**
 * Create Payment Intent Actor
 *
 * Creates a Stripe Payment Intent for kiosk payments.
 */

import { datadogLogs } from "@datadog/browser-logs";
import { fromPromise } from "xstate";
import { z } from "zod";
import { getKioskId } from "../../lib/kiosk-identity";

/**
 * Input Schema (What this actor needs)
 */
const CreatePaymentIntentInputSchema = z.object({
  amount: z.number().positive(), // Amount in dollars (not cents)
  user: z
    .object({
      id: z.string(),
      fname: z.string(),
      lname: z.string(),
    })
    .nullable(),
  orgName: z.string(),
  consumer: z
    .object({
      id: z.string(),
      stripeCustomerId: z.string().optional().nullable(),
    })
    .optional(),
  creationSource: z.string().optional(),
  existingPaymentMethodId: z.string().optional(),
  // Optional fields forwarded into PI metadata for webhook
  appointmentBtIds: z.array(z.string()).optional(),
  nonAppointmentBtIds: z.array(z.string()).optional(),
  paymentMethodType: z.string().optional(),
  amountCentsOverride: z.number().optional(),
  collectedBy: z.string().optional(),
});

type CreatePaymentIntentInput = z.infer<typeof CreatePaymentIntentInputSchema>;

/**
 * Success Schema (What this actor returns on success)
 */
const CreatePaymentIntentSuccessSchema = z.object({
  paymentIntentId: z.string(),
  clientSecret: z.string(),
  customerOptions: z
    .object({
      customer: z.string(),
      ephemeralKey: z.string(),
    })
    .optional(),
});

type CreatePaymentIntentSuccess = z.infer<
  typeof CreatePaymentIntentSuccessSchema
>;

/**
 * Error Schema (What this actor returns on failure)
 */
const CreatePaymentIntentErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "NETWORK_ERROR",
    "BACKEND_ERROR",
    "VALIDATION_ERROR",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
  details: z.unknown().optional(),
});

type CreatePaymentIntentError = z.infer<typeof CreatePaymentIntentErrorSchema>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
  composedActors?: readonly string[];
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Custom error class for payment intent creation
 */
export class PaymentIntentError extends Error {
  code:
    | "NETWORK_ERROR"
    | "BACKEND_ERROR"
    | "VALIDATION_ERROR"
    | "INVALID_INPUT"
    | "UNKNOWN_ERROR";
  details?: unknown;
  retryable: boolean;
  actorName: string;
  functionName: string;
  location?: string;

  constructor(
    message: string,
    code:
      | "NETWORK_ERROR"
      | "BACKEND_ERROR"
      | "VALIDATION_ERROR"
      | "INVALID_INPUT"
      | "UNKNOWN_ERROR",
    details?: unknown,
    location?: string,
  ) {
    super(message);
    this.name = "PaymentIntentError";
    this.code = code;
    this.details = details;
    this.retryable = code === "NETWORK_ERROR" || code === "BACKEND_ERROR";
    this.actorName = "createPaymentIntent";
    this.functionName = "createPaymentIntent";
    this.location = location;
  }
}

/**
 * Create Payment Intent helper function
 */
export const createPaymentIntent = async (
  params: CreatePaymentIntentInput,
): Promise<CreatePaymentIntentSuccess> => {
  if (!params.orgName) {
    throw new PaymentIntentError(
      "Organization name is required to create payment intent",
      "INVALID_INPUT",
      undefined,
    );
  }

  if (params.amount <= 0) {
    throw new PaymentIntentError(
      "Payment amount must be greater than 0",
      "INVALID_INPUT",
      undefined,
    );
  }

  try {
    const response = await fetch("/api/stripe/create-payment-intent", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        amount: params.amount, // Amount in dollars
        user: params.user,
        orgName: params.orgName,
        consumer: params.consumer,
        creationSource: params.creationSource,
        existingPaymentMethodId: params.existingPaymentMethodId,
        appointmentBtIds: params.appointmentBtIds,
        nonAppointmentBtIds: params.nonAppointmentBtIds,
        paymentMethodType: params.paymentMethodType,
        amountCentsOverride: params.amountCentsOverride,
        collectedBy: params.collectedBy,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new PaymentIntentError(
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        "BACKEND_ERROR",
        errorData,
      );
    }

    const data = await response.json();

    if (!data.paymentIntentId || !data.clientSecret) {
      throw new PaymentIntentError(
        "Invalid response from payment intent API",
        "BACKEND_ERROR",
        data,
      );
    }

    datadogLogs.logger.info("[Fleet] Payment intent created", {
      action: "payment_intent_created",
      metadata: {
        kioskId: getKioskId(),
        consumerId: params.consumer?.id ?? null,
        paymentIntentId: data.paymentIntentId,
        amount: params.amount,
      },
    });

    return {
      paymentIntentId: data.paymentIntentId,
      clientSecret: data.clientSecret,
      customerOptions: data.customerOptions,
    };
  } catch (error) {
    if (error instanceof PaymentIntentError) {
      throw error;
    }

    // Network or other errors
    throw new PaymentIntentError(
      (error as Error).message || "Failed to create payment intent",
      "NETWORK_ERROR",
      error,
    );
  }
};

/**
 * Create Payment Intent Actor Implementation
 */
const createPaymentIntentActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: CreatePaymentIntentInput;
  }): Promise<CreatePaymentIntentSuccess> => {
    // Validate input
    const validated = CreatePaymentIntentInputSchema.safeParse(input);

    if (!validated.success) {
      const errorData: CreatePaymentIntentError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, {
        ...errorData,
        actorName: "createPaymentIntent",
      });
      throw error;
    }

    try {
      const result = await createPaymentIntent(validated.data);

      return result;
    } catch (error) {
      if (error instanceof PaymentIntentError) {
        const errorData: CreatePaymentIntentError = {
          code: error.code,
          message: error.message,
          retryable: error.retryable,
          details: error.details,
        };
        const err = new Error(errorData.message);
        Object.assign(err, {
          ...errorData,
          actorName: "createPaymentIntent",
          functionName: "createPaymentIntent",
        });
        datadogLogs.logger.error("[Fleet] Payment intent creation failed", {
          action: "payment_intent_failed",
          metadata: {
            kioskId: getKioskId(),
            consumerId: input.consumer?.id ?? null,
            amount: input.amount,
            errorCode: (error as PaymentIntentError).code ?? "UNKNOWN_ERROR",
            errorMessage: (error as Error).message,
          },
        });
        throw err;
      }

      // Unknown error
      const errorData: CreatePaymentIntentError = {
        code: "UNKNOWN_ERROR",
        message: (error as Error).message || "Unknown error",
        retryable: true,
      };
      const err = new Error(errorData.message);
      Object.assign(err, {
        ...errorData,
        actorName: "createPaymentIntent",
      });
      datadogLogs.logger.error("[Fleet] Payment intent creation failed", {
        action: "payment_intent_failed",
        metadata: {
          kioskId: getKioskId(),
          consumerId: input.consumer?.id ?? null,
          amount: input.amount,
          errorCode: (error as PaymentIntentError).code ?? "UNKNOWN_ERROR",
          errorMessage: (error as Error).message,
        },
      });
      throw err;
    }
  },
);

/**
 * Create Payment Intent Actor with Metadata
 */
export const createPaymentIntentActor: ActorWithMetadata = {
  impl: createPaymentIntentActorImpl,
  metadata: {
    displayName: "createPaymentIntent",
    description: "Create a Stripe Payment Intent for kiosk payments",
    category: "data",
    retryable: true,
    timeout: 30000, // 30 seconds
    errorCodes: [
      "INVALID_INPUT",
      "NETWORK_ERROR",
      "BACKEND_ERROR",
      "VALIDATION_ERROR",
      "UNKNOWN_ERROR",
    ],
    inputSchema: CreatePaymentIntentInputSchema,
    successSchema: CreatePaymentIntentSuccessSchema,
    errorSchema: CreatePaymentIntentErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as CreatePaymentIntentSuccess;
      return {
        paymentIntentId: success.paymentIntentId,
        clientSecret: success.clientSecret,
        customerOptions: success.customerOptions,
      };
    },
    onError: (error: unknown) => {
      const err = error as CreatePaymentIntentError;
      return {
        paymentIntentError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
